import { QueryOptions } from '@apollo/client/core';
import BaseService from '@root/common/base/BaseService';
import { ServiceInterface } from '@root/common/types/service';
import getPodcastsFrontpage from '@root/modules/podcast/graphql/getPodcastsFrontpage.graphql';
import { dataLoader, RedisDataLoaderOptions } from '@root/libs/redis/dataloader/dataLoader';
import { Podcast, TTSCategory } from '@root/modules/podcast/types/podcast';

interface Response {
  popular: {
    items: Podcast[];
  };
  latest: {
    items: Podcast[];
  };
  tts: {
    items: TTSCategory[];
  };
  books: {
    blocks: {
      items: {
        blockArticles: {
          items: {
            article: Podcast;
          }[];
        };
      }[];
    };
  }[];
  radios: {
    items: Podcast[];
  };
  archive: {
    items: Podcast[];
  };
}

interface MostListenedPodcastsResponse {
  data: number[];
}

export const resort = (items: Podcast[], ids: number[]) => {
  if (!ids.length) {
    return items;
  }

  return items.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
};

export default class PodcastFeedService extends BaseService implements ServiceInterface {
  private async getPopularIds() {
    const isDev = !!process.env.FAKE_DOMAIN_ENABLED;
    const isTest = process.env.CHANNEL_CONFIG_ENV === 'test';
    const baseUrl = isDev ? process.env.DEV_PATH : isTest ? 'https://www.test.delfi.lt' : 'https://www.delfi.lt';
    const service = new BaseService();
    const apiProvider = service.createProvider('HTTP');
    try {
      const { data } = await apiProvider.request<MostListenedPodcastsResponse>({ url: `${baseUrl}/api/get-most-listened-podcasts` });

      return typeof data !== 'undefined' ? data : [];
    } catch (error) {
      return [];
    }
  }

  private async fetchFeed() {
    const popularIds: number[] = await this.getPopularIds();
    const variables = { popularIds };
    const options: QueryOptions = Object.assign({ query: getPodcastsFrontpage }, { variables });
    const dataLoaderOptions: RedisDataLoaderOptions = {
      remote: {
        expireTimeMs: 30000, // 30 seconds
        gracePeriodMs: 24 * 3600 * 1000, // 24 hours
        keyPrefix: 'page_feed',
      },
    };

    const requestWrapper = async (options: QueryOptions): Promise<Response | Error> => {
      const apiProvider = this.createProvider('GraphQL');
      apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

      const [response] = await apiProvider.query<Response>(options);

      this.throwGraphqlOrApolloErrorIfExists(response);

      return response.data;
    };

    const redisDataLoader = dataLoader<QueryOptions, Response | Error>(requestWrapper, dataLoaderOptions);
    const response: Response | Error = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

    if (response instanceof Error) {
      throw response;
    }

    return {
      popular: resort(response?.popular?.items, popularIds) || [],
      latest: response?.latest?.items || [],
      tts: response?.tts?.items || [],
      books: response?.books[0]?.blocks?.items[0]?.blockArticles?.items?.map((blockArticle) => blockArticle.article) || [],
      radios: response?.radios?.items || [],
      archive: response?.archive?.items || [],
    };
  }

  public async fetch() {
    try {
      const response = await this.fetchFeed();

      return response;
    } catch (error) {
      process.sentry?.captureException(`PodcastFeedService fetch failed`, {
        contexts: { ...(error.message && { message: error.message }), ...(error.cause && { cause: error.cause }) },
        tags: { 'process.type': process.server ? 'server' : 'client' },
      });
      this.handleError({ statusCode: 500, message: 'error.unexpected_system_error' });
    }
  }
}

export const podcastFeedService = new PodcastFeedService();

export function fetchPodcastFeedService({ req }: { req: PortalRootIncomingMessage }) {
  const podcastFeedService = new PodcastFeedService({ req });
  return podcastFeedService.fetch();
}
